<script setup lang="ts">
/**
 * Reusable voice-dictation control.
 *
 * Renders an icon-only mic button (no background circle). On the web, while
 * recording it expands inline to also show the active microphone label with a
 * dropdown chevron; clicking the chevron pauses the recording without
 * transcribing and opens an input picker. Selecting a device or closing
 * the picker resumes recording with the chosen input.
 *
 * In the native app there is no device choice: the mic becomes an orange
 * "Done" pill whose bars follow the real input level, and the words appear
 * live through `@live` (the composer shows them in the field). Tapping Done,
 * or pausing long enough for the recognizer to end, lands the text.
 *
 * Each instance owns its own `useVoiceDictation()`. The parent listens to
 * `@transcript` (once per dictation, even when the text repeats), `@live`,
 * `@error` (with its kind: a permission denial can offer Settings) and
 * `@status` to render the surrounding composer state.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import useVoiceDictation, { type DictationErrorKind } from '@/composables/useVoiceDictation'
import { isNativePlatform } from '@/utils/platform'
import { desktopPlatform } from '@/desktop/bridge'

export type DictationStatus = 'idle' | 'recording' | 'paused' | 'transcribing'

const props = defineProps<{
    /** Disable the mic entirely (e.g. while the parent is submitting). */
    disabled?: boolean
    /** Anchor the picker dropdown. */
    pickerAlign?: 'left' | 'right'
    /** Remove mic controls from tab order (reduces iOS keyboard form-navigation chrome). */
    excludeFromTabOrder?: boolean
    /** Font Awesome icon name without the style prefix (idle / paused). */
    icon?: string
}>()

const idleIcon = computed(() => props.icon || 'fa-microphone')

const chromeTabIndex = computed(() =>
    props.excludeFromTabOrder ? -1 : undefined,
)

const emit = defineEmits<{
    transcript: [text: string]
    error: [message: string, kind: DictationErrorKind]
    status: [status: DictationStatus]
    /** Native: the words heard so far while recording ('' when idle). */
    live: [text: string]
}>()

const native = isNativePlatform()

const {
    isRecording,
    isTranscribing,
    isPaused,
    voiceError,
    errorKind,
    liveText,
    level,
    availableInputs,
    selectedDeviceId,
    currentInputLabel,
    requestMicrophone,
    startRecordingWithStream,
    stopRecording,
    pauseForDeviceSwitch,
    resumeRecording,
    selectInputDevice,
    refreshInputs,
    cancel,
    setError,
} = useVoiceDictation({ onTranscript: (text) => emit('transcript', text) })

/** Waveform bars: each follows the input level with its own weight and a little drift, so it reads as live. */
const BAR_WEIGHTS = [0.55, 1, 0.75, 0.9, 0.5]
const bars = computed(() => {
    const l = level.value
    const t = Date.now() / 140
    return BAR_WEIGHTS.map((w, i) => {
        const wobble = 0.82 + 0.18 * Math.sin(t + i * 1.7)
        return Math.max(0.18, Math.min(1, 0.18 + l * 1.25 * w * wobble))
    })
})

const pickerOpen = ref(false)
const containerRef = ref<HTMLElement | null>(null)
const pickerPanelRef = ref<HTMLElement | null>(null)

/** Fixed positioning for the body-teleported picker (escapes overflow:hidden ancestors). */
const pickerStyle = ref<Record<string, string>>({})

const status = computed<DictationStatus>(() => {
    if (isTranscribing.value) return 'transcribing'
    if (isRecording.value) return 'recording'
    if (isPaused.value) return 'paused'
    return 'idle'
})

watch(status, (s) => emit('status', s), { immediate: true })

watch(liveText, (t) => emit('live', t))

watch(voiceError, (val) => {
    if (val) emit('error', val, errorKind.value ?? 'other')
})

async function startFromGesture() {
    try {
        const stream = await requestMicrophone()
        await startRecordingWithStream(stream)
    } catch (e) {
        const err = e as DOMException
        if (
            err?.name === 'NotAllowedError' ||
            err?.message?.includes('Permission denied')
        ) {
            setError(native ? 'Microphone access is off.' : micDeniedMessage(), 'permission')
        } else {
            setError(err?.message || 'Failed to start voice.')
        }
    }
}

/** Where to turn the mic back on: the OS privacy settings in the desktop app, the site permission on the web. */
function micDeniedMessage(): string {
    const os = desktopPlatform()
    if (os === 'mac') {
        return 'Microphone access is off. Allow Lighthouse to use the microphone in System Settings → Privacy & Security → Microphone, then try again.'
    }
    if (os === 'win') {
        return 'Microphone access is off. Allow Lighthouse in Settings → Privacy → Microphone, then try again.'
    }
    if (os === 'linux') {
        return 'Microphone access was denied. Check that Lighthouse can use your microphone in your system sound settings, then try again.'
    }
    return 'Microphone access was denied. Allow microphone for this site in your browser and try again.'
}

let ignoreStartUntil = 0

async function onMicClick() {
    if (props.disabled) return
    if (isTranscribing.value) return
    if (Date.now() < ignoreStartUntil) return
    if (pickerOpen.value) {
        // If the picker is open, treat the mic click as "I'm done — close
        // picker and just transcribe whatever I have (nothing, since paused)".
        pickerOpen.value = false
        if (isPaused.value) await resumeRecording()
        return
    }
    if (isRecording.value) {
        ignoreStartUntil = Date.now() + 400
        await stopRecording()
        return
    }
    if (isPaused.value) {
        await resumeRecording()
        return
    }
    await startFromGesture()
}

function repositionPicker() {
    const anchor = containerRef.value
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    const margin = 8
    const minH = 160
    const spaceAbove = rect.top
    const spaceBelow = window.innerHeight - rect.bottom
    const openAbove = spaceAbove >= minH || spaceAbove > spaceBelow

    const style: Record<string, string> = {
        position: 'fixed',
        zIndex: '10050',
        minWidth: '240px',
        maxWidth: '320px',
    }

    if (openAbove) {
        style.bottom = `${window.innerHeight - rect.top + margin}px`
        style.top = 'auto'
        style.maxHeight = `${Math.max(120, spaceAbove - margin * 2)}px`
    } else {
        style.top = `${rect.bottom + margin}px`
        style.bottom = 'auto'
        style.maxHeight = `${Math.max(120, spaceBelow - margin * 2)}px`
    }

    if (props.pickerAlign === 'right') {
        style.right = `${window.innerWidth - rect.right}px`
        style.left = 'auto'
    } else {
        style.left = `${rect.left}px`
        style.right = 'auto'
    }

    pickerStyle.value = style
}

function onWindowReposition() {
    if (pickerOpen.value) repositionPicker()
}

async function openPicker() {
    if (props.disabled || isTranscribing.value) return
    if (isRecording.value) {
        await pauseForDeviceSwitch()
    }
    pickerOpen.value = true
    await refreshInputs()
    await nextTick()
    repositionPicker()
}

async function closePicker(resume = true) {
    if (!pickerOpen.value) return
    pickerOpen.value = false
    if (resume && isPaused.value) {
        await resumeRecording()
    }
}

async function onChoose(deviceId: string) {
    if (deviceId === (selectedDeviceId.value ?? '')) {
        await closePicker(true)
        return
    }
    await selectInputDevice(deviceId)
    await closePicker(true)
}

async function onUseSystemDefault() {
    await selectInputDevice(null)
    await closePicker(true)
}

function onDocPointer(e: PointerEvent) {
    if (!pickerOpen.value) return
    const t = e.target as Node | null
    const inAnchor = containerRef.value?.contains(t ?? null)
    const inPicker = pickerPanelRef.value?.contains(t ?? null)
    if (!inAnchor && !inPicker) {
        void closePicker(true)
    }
}

function onDocKey(e: KeyboardEvent) {
    if (e.key === 'Escape' && pickerOpen.value) {
        void closePicker(true)
    }
}

onMounted(() => {
    document.addEventListener('pointerdown', onDocPointer)
    document.addEventListener('keydown', onDocKey)
    window.addEventListener('resize', onWindowReposition)
    window.addEventListener('scroll', onWindowReposition, true)
    // Pre-populate device labels if permission was previously granted.
    void refreshInputs()
})

onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', onDocPointer)
    document.removeEventListener('keydown', onDocKey)
    window.removeEventListener('resize', onWindowReposition)
    window.removeEventListener('scroll', onWindowReposition, true)
    cancel()
})

watch(pickerOpen, (open) => {
    if (open) {
        void nextTick(() => repositionPicker())
    }
})

/** Reflow after the device list populates (changes panel height). */
watch(
    () => availableInputs.value.length,
    () => {
        if (pickerOpen.value) {
            void nextTick(() => repositionPicker())
        }
    },
)

defineExpose({ cancel })
</script>

<template>
    <div ref="containerRef" class="dictation-control">
        <!-- Active input chip (web only, while recording / paused): there's no device choice on native. -->
        <button
            v-if="(isRecording || isPaused) && !native"
            type="button"
            class="dictation-chip"
            :class="isPaused ? 'dictation-chip--paused' : ''"
            :tabindex="chromeTabIndex"
            :title="isPaused ? 'Recording paused — pick an input' : 'Switch microphone'"
            @click="pickerOpen ? closePicker(true) : openPicker()"
        >
            <span class="dictation-chip-label">{{ currentInputLabel }}</span>
            <i
                class="fa-solid fa-chevron-down dictation-chip-chevron"
                :class="pickerOpen ? 'is-open' : ''"
            ></i>
        </button>

        <!-- Mic icon button (no background) -->
        <button
            type="button"
            :disabled="disabled || isTranscribing"
            class="dictation-mic"
            :tabindex="chromeTabIndex"
            :class="{
                'dictation-mic--recording': isRecording,
                'dictation-mic--recording-native': isRecording && native,
                'dictation-mic--paused': isPaused,
                'dictation-mic--busy': isTranscribing,
            }"
            :title="
                isTranscribing
                    ? 'Transcribing…'
                    : isRecording
                      ? (native ? 'Done' : 'Stop and transcribe')
                      : isPaused
                        ? 'Resume recording'
                        : 'Dictate'
            "
            :aria-label="isRecording ? (native ? 'Done dictating' : 'Stop recording and transcribe') : 'Start dictation'"
            @click="onMicClick"
        >
            <span
                v-if="isRecording && native"
                class="dictation-visualizer-wrap"
                aria-hidden="true"
            >
                <span class="dictation-visualizer">
                    <span
                        v-for="(b, n) in bars"
                        :key="n"
                        class="dictation-visualizer-bar"
                        :style="{ transform: `scaleY(${b})` }"
                    />
                </span>
                <span class="dictation-stop-label">Done</span>
            </span>
            <i
                v-if="isTranscribing"
                class="fa-light fa-spinner fa-spin"
            ></i>
            <span v-else-if="isRecording && !native" class="inline-flex items-center gap-1">
                <i class="fa-solid fa-stop"></i>
                <span class="dictation-stop-label">Stop</span>
            </span>
            <i
                v-else-if="isPaused"
                class="fa-solid"
                :class="idleIcon"
            ></i>
            <i
                v-else-if="!isRecording"
                class="fa-solid"
                :class="idleIcon"
            ></i>
        </button>

    </div>

    <teleport to="body">
        <div
            v-if="pickerOpen"
            ref="pickerPanelRef"
            class="dictation-picker dictation-picker--portal"
            :style="pickerStyle"
            role="menu"
        >
            <div class="dictation-picker-header">
                Microphone
                <span class="dictation-picker-hint">Recording paused</span>
            </div>
            <button
                type="button"
                class="dictation-picker-item"
                :class="selectedDeviceId === null ? 'is-selected' : ''"
                @click="onUseSystemDefault"
            >
                <i class="fa-solid fa-check dictation-picker-check" :class="selectedDeviceId === null ? 'is-visible' : ''"></i>
                <span class="dictation-picker-label">System default</span>
            </button>
            <div v-if="availableInputs.length === 0" class="dictation-picker-empty">
                No input devices detected.
            </div>
            <button
                v-for="d in availableInputs"
                :key="d.deviceId"
                type="button"
                class="dictation-picker-item"
                :class="selectedDeviceId === d.deviceId ? 'is-selected' : ''"
                @click="onChoose(d.deviceId)"
            >
                <i
                    class="fa-solid fa-check dictation-picker-check"
                    :class="selectedDeviceId === d.deviceId ? 'is-visible' : ''"
                ></i>
                <span class="dictation-picker-label">{{ d.label || 'Unnamed input' }}</span>
            </button>
        </div>
    </teleport>
</template>

<style scoped>
.dictation-control {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

/* Mic button: icon only, no background circle. */
.dictation-mic {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    background: transparent;
    color: rgb(115, 115, 115);
    font-size: 13px;
    cursor: pointer;
    padding: 0;
    transition: color 120ms ease, transform 120ms ease;
}
.dictation-mic:hover:not(:disabled) {
    color: rgb(38, 38, 38);
}
.dictation-mic:active:not(:disabled) {
    transform: scale(0.94);
}
.dictation-mic:disabled {
    color: rgb(200, 200, 200);
    cursor: not-allowed;
}
.dictation-mic--recording {
    color: rgb(239, 68, 68);
    width: auto;
    min-width: 28px;
    padding: 0 8px;
    gap: 4px;
    animation: dictation-pulse 1.2s ease-in-out infinite;
}
.dictation-stop-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.01em;
}
.dictation-mic--recording:hover:not(:disabled) {
    color: rgb(220, 38, 38);
}
.dictation-mic--paused {
    color: rgb(234, 88, 12);
}
.dictation-mic--paused:hover:not(:disabled) {
    color: rgb(194, 65, 12);
}

/* Native recording: filled orange pill + live bars + “tap to transcribe”. */
.dictation-mic--recording-native {
    animation: none;
    width: auto;
    min-width: 0;
    height: 32px;
    padding: 0 12px 0 10px;
    gap: 8px;
    border-radius: 999px;
    color: #fff;
    background: linear-gradient(180deg, #ff6a3d 0%, #fb4c1f 100%);
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.45),
        0 4px 10px rgba(251, 76, 31, 0.35);
}
.dictation-mic--recording-native:hover:not(:disabled) {
    color: #fff;
}
.dictation-mic--recording-native .dictation-stop-label {
    color: #fff;
    font-size: 12px;
    font-weight: 650;
    letter-spacing: -0.01em;
    white-space: nowrap;
}
.dictation-visualizer-wrap {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}
.dictation-visualizer {
    display: inline-flex;
    align-items: center;
    gap: 2.5px;
    height: 16px;
}
/* Bars follow the real input level (scaleY from the recognizer's level events). */
.dictation-visualizer-bar {
    width: 3px;
    height: 16px;
    border-radius: 2px;
    background: #fff;
    transform: scaleY(0.18);
    transform-origin: center;
    transition: transform 110ms linear;
    will-change: transform;
}
@media (prefers-reduced-motion: reduce) {
    .dictation-visualizer-bar {
        transition: none;
    }
}

/* Active-input chip with chevron */
.dictation-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 24px;
    padding: 0 8px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: rgb(250, 250, 250);
    color: rgb(82, 82, 82);
    border-radius: 999px;
    font-size: 11px;
    line-height: 1;
    cursor: pointer;
    max-width: 180px;
    transition: background 120ms ease, color 120ms ease, border-color 120ms ease;
}
.dictation-chip:hover {
    background: rgb(243, 243, 243);
    color: rgb(38, 38, 38);
    border-color: rgba(0, 0, 0, 0.14);
}
.dictation-chip--paused {
    background: rgb(255, 247, 237);
    border-color: rgba(234, 88, 12, 0.3);
    color: rgb(154, 52, 18);
}
.dictation-chip-label {
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.dictation-chip-chevron {
    font-size: 9px;
    transition: transform 120ms ease;
}
.dictation-chip-chevron.is-open {
    transform: rotate(180deg);
}

/* Picker menu (position + z-index come from inline `pickerStyle` on the portal). */
.dictation-picker {
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06);
    padding: 6px;
    box-sizing: border-box;
}
.dictation-picker--portal {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}
.dictation-picker-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 8px 8px;
    font-size: 11px;
    font-weight: 600;
    color: rgb(64, 64, 64);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.dictation-picker-hint {
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    color: rgb(234, 88, 12);
    font-size: 10px;
}
.dictation-picker-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 8px;
    border: none;
    background: transparent;
    text-align: left;
    font-size: 13px;
    color: rgb(38, 38, 38);
    border-radius: 6px;
    cursor: pointer;
    transition: background 120ms ease;
}
.dictation-picker-item:hover {
    background: rgb(245, 245, 245);
}
.dictation-picker-item.is-selected {
    color: rgb(20, 20, 20);
    font-weight: 500;
}
.dictation-picker-check {
    width: 12px;
    font-size: 11px;
    color: rgb(234, 88, 12);
    opacity: 0;
}
.dictation-picker-check.is-visible {
    opacity: 1;
}
.dictation-picker-label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.dictation-picker-empty {
    padding: 8px;
    font-size: 12px;
    color: rgb(140, 140, 140);
}

@keyframes dictation-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.65; }
}
</style>
